import React, { useState } from 'react';
import {
    DialogActions,
    DialogContent,
    DialogTitle,
    Dialog,
    Alert,
    TextField,
    Typography,
    Box,
    CircularProgress,
} from '@mui/material';
import CommonButton from '../components/atom/CommonButton';
import { useTranslation } from 'react-i18next';
import { finishManualRefund } from '@openapi/transactions/transactions';
import useNotify from '../hooks/useNotify';
import { formatCentsToUsd } from '../helpers/formatCentsToUsd';
import { formatDateTimeInVenueTz } from '../helpers/formatDateTime';

const ManualRefundModal = ({ transaction, onSuccess, showModal, MODAL_TYPE }) => {
    const { t } = useTranslation();
    const { notifySuccess, notifyError } = useNotify();
    const [note, setNote] = useState('');
    const [loading, setLoading] = useState(false);

    const closeAction = () => showModal(MODAL_TYPE.NONE);

    const handleConfirm = async () => {
        try {
            setLoading(true);

            await finishManualRefund(transaction.id, {
                note: note.trim() || undefined,
            });

            notifySuccess(t('manualrefunds.refund_success'));
            if (onSuccess) onSuccess();
            closeAction();
        } catch (error) {
            console.error('Manual refund error:', error);
            notifyError(error?.response?.data?.message || t('manualrefunds.refund_error'));
        } finally {
            setLoading(false);
        }
    };

    if (!transaction) {
        return null;
    }

    return (
        <Dialog open onClose={closeAction} maxWidth="sm" fullWidth>
            <DialogTitle>{t('manualrefunds.modal_title')}</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        <strong>{t('transactions.columns.id')}:</strong> {transaction.id}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        <strong>{t('transactions.columns.showName')}:</strong>{' '}
                        {transaction.showTitle}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        <strong>{t('transactions.columns.startTime')}:</strong>{' '}
                        {formatDateTimeInVenueTz(transaction.startTime)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        <strong>{t('transactions.columns.booker')}:</strong>{' '}
                        {transaction.bookerName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        <strong>{t('transactions.columns.amount')}:</strong>{' '}
                        {formatCentsToUsd(-1 * transaction.amount)}
                    </Typography>
                </Box>

                <Alert severity="warning" sx={{ mb: 3 }}>
                    {t('manualrefunds.modal_warning')}
                </Alert>

                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label={t('manualrefunds.note_label')}
                    placeholder={t('manualrefunds.note_placeholder')}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    sx={{ mb: 2 }}
                />
            </DialogContent>
            <DialogActions>
                <CommonButton
                    buttonType="secondary"
                    onClick={closeAction}
                    disabled={loading}
                    label={t('common.cancel')}
                />
                <CommonButton
                    onClick={handleConfirm}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                    label={t('manualrefunds.confirm_refund')}
                />
            </DialogActions>
        </Dialog>
    );
};

export default ManualRefundModal;
